import type { ReactElement, ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Badge, Button, Mascot, Modal, cn } from '@xdocs/ui';
import { JobProgressCard } from '../jobs/index.js';
import { useEnhancementT } from './useEnhancementT.js';
import styles from './EnhancementPanelShell.module.css';

/** F2/F3/F4/F17 shared panel lifecycle — mirrors FEATURES.md F2/F3/F4/F17
 * "UI states" almost verbatim: locked-for-Free, form, queued/streaming,
 * success, error/retry. */
export type EnhancementPanelPhase = 'form' | 'starting' | 'running' | 'done' | 'upsell' | 'error';

export interface EnhancementPanelShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  blurb: string;
  phase: EnhancementPanelPhase;
  jobId: string | null;
  doneTitle: string;
  errorMessage?: string | null;
  ctaLabel: string;
  creditsLabel?: string | undefined;
  ctaDisabled?: boolean;
  onStart: () => void;
  onMaybeLater: () => void;
  onDone: () => void;
  onRetryFromError: () => void;
  /** The action-specific form fields (F2 "how much"/direction, F3 cut-point,
   * F4 level, F17 language) — rendered only in the `form` phase. */
  children: ReactNode;
}

/**
 * Shared shell for the four PRO "killer feature" action panels (F2/F3/F4/
 * F17). Owns the Modal, the always-visible PRO badge (FEATURES.md 0.4: "Lock
 * badge on the control"), and the phase state machine. The `upsell` phase
 * (FEATURES.md 0.4 "inline upsell ... Never a dead control") fires reactively
 * off a `TIER_REQUIRED` reply from the start op — never a client-side
 * pre-block, since the client has no signal of the caller's own tier yet
 * (`document.dto.ts`'s documented contract: "never blocked at preview time").
 */
export function EnhancementPanelShell({
  open,
  onOpenChange,
  title,
  blurb,
  phase,
  jobId,
  doneTitle,
  errorMessage,
  ctaLabel,
  creditsLabel,
  ctaDisabled = false,
  onStart,
  onMaybeLater,
  onDone,
  onRetryFromError,
  children,
}: EnhancementPanelShellProps): ReactElement {
  const { t } = useEnhancementT();
  const navigate = useNavigate();

  return (
    <Modal open={open} onClose={() => onOpenChange(false)} title={title} size="sm">
      <div className={cn(styles['titleRow'])}>
        <Badge tone="pro">{t('enhancement:pro.badge')}</Badge>
      </div>
      {phase === 'upsell' && (
        <div className={styles['upsell']}>
          <Mascot pose="concerned" size={80} alt="" muted />
          <p className={styles['upsellLabel']}>{t('enhancement:upsell.title')}</p>
          <p className={styles['upsellBlurb']}>{blurb}</p>
          <Button variant="primary" className={styles['cta']} onClick={() => void navigate('/settings/billing')}>
            {t('enhancement:upsell.upgrade')}
          </Button>
          <Button variant="ghost" onClick={onMaybeLater}>
            {t('enhancement:upsell.maybeLater')}
          </Button>
        </div>
      )}

      {phase === 'form' && (
        <div className={styles['form']}>
          <p className={styles['blurb']}>{blurb}</p>
          {children}
          {creditsLabel && (
            <p className={styles['costLine']}>
              <span>{creditsLabel}</span>
            </p>
          )}
          <Button variant="primary" className={styles['cta']} onClick={onStart} disabled={ctaDisabled}>
            {ctaLabel}
          </Button>
        </div>
      )}

      {phase === 'starting' && (
        <div className={styles['running']} aria-busy="true">
          <div className={styles['workingRow']}>
            <Loader2 size={20} className="lucide-spin" aria-hidden="true" />
            <span>{t('enhancement:running.working')}</span>
          </div>
        </div>
      )}

      {phase === 'running' && jobId && (
        <div className={styles['running']}>
          <JobProgressCard jobId={jobId} />
          <p className={styles['checkpointedNote']}>{t('enhancement:running.checkpointed')}</p>
        </div>
      )}

      {phase === 'done' && (
        <div className={styles['done']}>
          <span className={styles['checkIcon']} aria-hidden="true">
            <Check size={20} />
          </span>
          <p className={styles['doneTitle']}>{doneTitle}</p>
          <p className={styles['doneNote']}>{t('enhancement:done.savedAsNewVersion')}</p>
          <Button variant="primary" onClick={onDone}>
            {t('enhancement:done.close')}
          </Button>
        </div>
      )}

      {phase === 'error' && (
        <div className={styles['errorState']}>
          <p role="alert">{errorMessage}</p>
          <Button variant="secondary" onClick={onRetryFromError}>
            {t('enhancement:error.retry')}
          </Button>
        </div>
      )}
    </Modal>
  );
}
